import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { TasksPage } from './pages/TasksPage';
import { TaskFormPage } from './pages/TaskFormPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { TeamPage } from './pages/TeamPage';
import { OrganogramPage } from './pages/OrganogramPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { ActivityLogsPage } from './pages/ActivityLogsPage';
import { BackupPage } from './pages/BackupPage';
import { SettingsPage } from './pages/SettingsPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { CalendarPage } from './pages/CalendarPage';
import { WorkloadPage } from './pages/WorkloadPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import CategoryManagementPage from './pages/CategoryManagementPage';
import SubCategoryManagementPage from './pages/SubCategoryManagementPage';
import BulkTaskPage from './pages/BulkTaskPage';
import ApprovalsPage from './pages/ApprovalsPage';
import { AccessControlPage } from './pages/AccessControlPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { UniAIPage } from './pages/UniAIPage';
import { FrequencyShell } from './pages/FrequencyPages';
import { FrequencyAckPage, FrequencyQueryPage } from './pages/FrequencyPublicPages';

// Layout
import { MainLayout } from './components/MainLayout';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <MainLayout>{children}</MainLayout>;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      {/* The Frequency public magic-link pages (no auth) */}
      <Route path="/frequency/ack/:token" element={<FrequencyAckPage />} />
      <Route path="/frequency/query/:token" element={<FrequencyQueryPage />} />
      
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tasks" element={
        <ProtectedRoute>
          <TasksPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tasks/new" element={
        <ProtectedRoute>
          <TaskFormPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tasks/:id" element={
        <ProtectedRoute>
          <TaskDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tasks/:id/edit" element={
        <ProtectedRoute>
          <TaskFormPage />
        </ProtectedRoute>
      } />
      
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectsPage />
        </ProtectedRoute>
      } />

      <Route path="/uni-ai" element={
        <ProtectedRoute>
          <UniAIPage />
        </ProtectedRoute>
      } />
      
      <Route path="/team" element={
        <ProtectedRoute>
          <TeamPage />
        </ProtectedRoute>
      } />
      <Route path="/organogram" element={
        <ProtectedRoute>
          <OrganogramPage />
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      
      <Route path="/workload" element={
        <ProtectedRoute>
          <WorkloadPage />
        </ProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/employees" element={
        <ProtectedRoute>
          <EmployeesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/categories" element={
        <ProtectedRoute>
          <CategoryManagementPage />
        </ProtectedRoute>
      } />
      
      <Route path="/sub-categories" element={
        <ProtectedRoute>
          <SubCategoryManagementPage />
        </ProtectedRoute>
      } />
      
      <Route path="/bulk-tasks" element={
        <ProtectedRoute>
          <BulkTaskPage />
        </ProtectedRoute>
      } />
      
      <Route path="/approvals" element={
        <ProtectedRoute>
          <ApprovalsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/activity-logs" element={
        <ProtectedRoute>
          <ActivityLogsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/backup" element={
        <ProtectedRoute>
          <BackupPage />
        </ProtectedRoute>
      } />

      <Route path="/access-control" element={
        <ProtectedRoute>
          <AccessControlPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />

      {/* The Frequency (authenticated module) */}
      <Route path="/frequency/*" element={
        <ProtectedRoute>
          <FrequencyShell />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
